import { z } from 'zod';
/**
 * M3 — le seul `status` de tout le modèle : la position dans le cycle de vie. Tout ce qui lui
 * ressemble ailleurs est un `result`, écrit une fois, ou un prédicat calculé.
 *
 * ⚠️ Le mot `state` a été **supprimé du modèle**, valeurs comprises : le prototype y mêlait
 * trois natures différentes, et le garder pour l'une d'elles laisserait l'ambiguïté vivante.
 */
export const INQUIRY_STATUSES = [
    'received',
    'assigned',
    'in_progress',
    'submitted',
    'validated',
    'archived',
    'dropped',
];
export const inquiryStatusSchema = z.enum(INQUIRY_STATUSES);
/**
 * Ce que chaque statut **s'imprime** — sur un écran comme dans un export du tableau.
 *
 * ⚠️ **Des libellés, pas des identifiants** : ils sont en français par la troisième exception du
 * glossaire (« libellés imprimés »), et c'est le vocabulaire métier — celui des réunions et des
 * prototypes, jamais la valeur d'énumération.
 *
 * ⚠️ Ils vivent dans le domaine parce qu'ils ont **deux lecteurs qui ne peuvent pas s'importer**
 * l'un l'autre : l'export du tableau, composé par l'API, et l'écran « Demandes ». Sept mots
 * recopiés des deux côtés, c'est un fichier qui cesse de dire ce que la colonne « État »
 * affichait.
 */
export const INQUIRY_STATUS_LABELS = {
    received: 'Reçue',
    assigned: 'Affectée',
    in_progress: 'En contrôle',
    submitted: 'À valider',
    validated: 'Validé',
    archived: 'Archivé',
    dropped: 'Abandonnée',
};
/**
 * M4 — transitions **recommandées, jamais imposées**. L'interface met ces statuts en avant et
 * range les autres derrière un « autre statut » ; l'API, elle, n'oppose aucun refus : elle
 * applique la transition inattendue et la **journalise** comme inhabituelle.
 *
 * C'est délibéré : le métier a des cas de reprise et de rattrapage qu'on ne connaît pas tous,
 * et une garde trop rigide se paierait en contournements. Ce sont ces journaux qui diront, dans
 * six mois, lesquelles méritent une vraie garde — décider maintenant serait deviner.
 *
 * Deux entrées ne sont pas des marches avant, et c'est le cœur de la fiche : `submitted →
 * in_progress` est le renvoi au terrain d'un contrôle à corriger, `validated → submitted` la
 * réédition d'un contrôle déjà validé.
 */
export const SUGGESTED_NEXT = {
    received: ['assigned', 'dropped'],
    assigned: ['in_progress', 'received', 'dropped'],
    in_progress: ['submitted', 'dropped'],
    submitted: ['validated', 'in_progress'],
    validated: ['archived', 'submitted'],
    archived: [],
    dropped: ['received'],
};
/**
 * `SUGGESTED_NEXT` lu par une fonction plutôt que déréférencé chez l'appelant — c'est ce qui
 * évite qu'un module recopie la liste « en attendant » et la laisse diverger.
 *
 * ⚠️ **Ce n'est pas une garde de transition, et s'en servir comme telle donne une mauvaise
 * réponse.** M4 ci-dessus est explicite : l'API n'oppose aucun refus sur une transition
 * inattendue. La fonction répond à « M4 recommande-t-elle cette marche ? », ce qui sert à mettre
 * un statut en avant dans une interface — et à ce qu'un geste métier nommé puisse **affirmer** que
 * la marche qu'il fait franchir est bien déclarée ici, sous test.
 *
 * Elle ne suffit pas à autoriser un geste, parce qu'une même marche peut être recommandée depuis
 * plusieurs statuts : `assigned → in_progress` l'est aussi, si bien qu'un renvoi au terrain qui ne
 * consulterait que cette table passerait sur une demande où **rien n'a jamais été transmis**. Un
 * geste nommé compare le statut réel à son point de départ ; cette fonction dit seulement que le
 * point de départ et l'arrivée qu'il s'est choisis sont ceux du cycle de vie.
 */
export function isSuggestedTransition(from, to) {
    return SUGGESTED_NEXT[from].includes(to);
}
/**
 * La répartition des demandes par position du cycle de vie (M3) — ce que le tableau de bord
 * affiche, **compté en base** et jamais dans le navigateur (D24).
 *
 * ⚠️ **Les sept statuts sont exigés, `dropped` compris.** `z.record` sur une énumération réclame
 * toutes ses clés : un statut absent de la réponse ne peut donc pas se lire « zéro demande » à
 * l'écran alors qu'il veut dire « le serveur ne l'a pas compté ». Le jour où M3 gagne un statut,
 * c'est ce contrat qui tombe en premier.
 *
 * Ce que l'écran fait de `dropped` lui appartient : une file d'abandon n'a pas sa place dans un
 * histogramme d'avancement, où elle se lirait comme un retard.
 */
export const inquiryStatusBreakdownSchema = z.record(inquiryStatusSchema, z.number().int().min(0));
//# sourceMappingURL=status.js.map